<template>
  <div am-formula-modal>
    <div class="_background:black" am-formula-modal-bg @click="close()" />
    <i-card am-formula-modal-body>
      <template #header>
        <div class="_display:flex _align-items:center _margin-bottom:1">
          <p class="_margin:1/4 _flex:1">Formula</p>
          <i-icon am-icon-close name="ink-times" @click="close()" />
        </div>
        <div
          v-if="!store.state.formula.error && !store.state.formula.loading"
          am-first
          class="_display:flex _align-items:center _justify-content:space-between"
        >
          <ICheckbox v-model="checked">only for current category</ICheckbox>
          <div
            v-if="store.state.user.role === 'ADMIN' || store.state.user.role === 'CONSTRUCTOR'"
            class="_margin-left:1/4"
          >
            <IButton
              v-if="!edited && !store.state.formula.loading"
              color="primary"
              size="sm"
              @click="edited = true"
            >
              Edit
            </IButton>
            <div v-else class="_display:flex">
              <IButton
                v-if="Object.keys(store.state.formula.parConfEdited).length"
                color="primary"
                size="sm"
                class="_margin-right:1/4"
                @click="store.dispatch('formula/saveFormula')"
              >
                Save
              </IButton>
              <IButton
                size="sm"
                color="info"
                @click="addParam()"
              >
                Add parameter
                <i-icon name="ink-plus" class="_margin-left:1/2" />
              </IButton>
            </div>
          </div>
        </div>
        <div
          am-error
          class="_background:danger"
          v-if="Object.keys(store.state.formula.errors).length"
        >
          <div class="_display:flex _align-items:center">
            <IIcon name="ink-danger" class="_margin-right:1/4" />
            <div>
              <p
                v-for="key in store.state.formula.errors[store.state.formula.errorKey]"
                :key="key"
                class="_margin:0"
              >
                - {{ key }}
              </p>
            </div>
          </div>
          <div
            class="_display:flex _align-items:center _justify-content:center _margin-top:1/4"
            v-if="Object.keys(store.state.formula.errors).length > 1"
          >
            <IButton
              circle
              size="sm"
              :color="n === store.state.formula.errorKey ? 'light' : ''"
              v-for="(n, index) in Object.keys(store.state.formula.errors)"
              :key="n"
              class="_margin-right:1/4"
              @click="store.state.formula.errorKey = n"
            >
              {{index + 1}}
            </IButton>
          </div>
        </div>
      </template>
      <div am-edit class="_padding-top:1/2 _padding-bottom:2">
        <div am-block>
          <div
            v-if="store.state.formula.loading"
            class="_align-self:stretch _display:flex _justify-content:center _align-items:center _height:100%"
          >
            <i-loader color="primary" />
          </div>
          <div
            v-else-if="store.state.formula.error"
            class="_display:flex _justify-content:center _align-items:center _flex-direction:column"
          >
            <p>Something went wrong, formula didn't load</p>
            <IButton
              size="sm"
              color="primary"
              @click="store.dispatch('formula/getFormula')"
            >
              Reload
            </IButton>
          </div>
          <template v-else>
            <i-card
              v-for="item in sortedDataNumber"
              :key="item.id"
              class="_margin-bottom:1/2"
              :id="'cardId_' + item.id"
            >
              <div :class="`_display:flex ${edited ? '_margin-bottom:1/2': ''}`">
                <p
                  v-if="!edited || !item.isNew"
                  class="_text:semibold _margin-right:1/2 _margin-bottom:1/2 _margin-top:1/2"
                  style="white-space:nowrap"
                >
                  {{ Number(item.number) ? 'par ' : ''}}{{ item.number }} -
                </p>
                <div v-if="edited && item.isNew" style="width:160px;" class="_display:flex _align-items:center">
                  <i-input
                    placeholder="number"
                    v-model="item.number"
                    @change="store.dispatch('formula/putParEdited', {id: item.id, data: item.number, tag: 'number'})"
                  />
                  <p
                    class="_text:semibold _margin-left:1/2 _margin-right:1/2 _margin-top:0 _margin-bottom:0"
                  >-</p>
                </div>
                <p v-if="!edited" class="_text:semibold _margin-bottom:1/2 _margin-top:1/2">
                  {{ item.description }}
                </p>
                <div v-else class="_width:100%">
                  <i-input
                    placeholder="description"
                    v-model="item.description"
                    @change="store.dispatch('formula/putParEdited', {id: item.id, data: item.description, tag: 'description'})"
                  />
                </div>
              </div>
              <formulaCategory
                :id="item.id"
                :category="item.category_codes"
                :edited="edited"
                @add="(code) => item.category_codes.push(code)"
                @del="(delIndex) => item.category_codes.splice(delIndex, 1)"
              />
              <formulaMeaning
                :id="item.id"
                :meaning="item.newMeaning"
                :edited="edited"
                @add="() => item.newMeaning.push({
                  key: '',
                  value: ''
                })"
                @del="(delIndex) => item.newMeaning.splice(delIndex, 1)"
              />
              <IButton
                v-if="edited"
                size="sm"
                class="_margin-top:1/4"
                @click="showModal(item.id, item.number)"
              >
                Delete
              </IButton>
            </i-card>
          </template>
        </div>
      </div>
    </i-card>
    <warning-modal
      v-if="isShowDeleteModal"
      :title="`Are you sure you want to delete ${deletedNumber}`"
      @delete="del"
      @cancel="cancel"
      v-model="isShowDeleteModal"
    />
    <warning-modal
      v-if="isShowChangesModal"
      title="What to do with the changed data"
      is-changed
      @delete="delChangedData"
      @cancel="saveChangedData"
      v-model="isShowChangesModal"
    />
  </div>
</template>

<script setup>
import { useStore } from 'vuex'
import { onMounted, onUnmounted, ref, computed, watch } from 'vue'

import warningModal from './warningModal'
import formulaMeaning from './formulaMeaning'
import formulaCategory from './formulaCategory'

const store = useStore()
const checked = ref(false)
const edited = ref(false)

const isShowDeleteModal = ref(false)
const deletedId = ref(null)
const deletedNumber = ref(null)
const showModal = (id, number) => {
  isShowDeleteModal.value = true
  deletedId.value = id
  deletedNumber.value = `${Number(number) ? 'par' : ''} ${number}`
}
const del = () => {
  store.dispatch('formula/deleteFormula', deletedId.value)
  isShowDeleteModal.value = null
}
const cancel = () => {
  isShowDeleteModal.value = null
}

const sortedDataNumber = computed(() => {
  if (checked.value) {
    return store.getters['formula/sortedDataNumber'](store.state.partCategories.selectedSubCategory)
  } else return store.getters['formula/sortedDataNumber'](null)
})
watch(
  () => store.state.formula.errorKey,
  (key) => {
    const card = document.getElementById(`cardId_${key}`)
    card.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }
)
watch(
  () => store.state.formula.errors,
  (errors) => {
    if (Object.keys(errors).length) {
      const card = document.getElementById(`cardId_${Object.keys(errors)[0]}`)
      card.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
  }
)
const timer = ref(null)
const addParam = () => {
  store.state.formula.newIndex++
  const data = {
    number: '',
    description: '',
    newMeaning: [],
    category_codes: []
  }
  store.state.formula.data.push({
    ...data,
    isNew: true,
    id: 'new' + store.state.formula.newIndex
  })
  store.state.formula.parConfEdited['new' + store.state.formula.newIndex] = { ...data }
  timer.value = window.setTimeout(() => {
    scrollToNew()
  }, 50)
}
const scrollToNew = () => {
  const card = document.getElementById(`cardId_new${store.state.formula.newIndex}`)
  card.scrollIntoView({ block: 'start', behavior: 'smooth' })
  clearTimeout(timer.value)
}
const isShowChangesModal = ref(false)
const delChangedData = () => {
  isShowChangesModal.value = false
  store.state.formula.parConfEdited = {}
  store.dispatch('formula/getFormula')
  close()
}
const saveChangedData = () => {
  isShowChangesModal.value = false
  store.dispatch('formula/saveFormula')
}
const close = () => {
  if (Object.keys(store.state.formula.parConfEdited).length) {
    isShowChangesModal.value = true
  } else store.state.formula.isShowFormula = null
}
onMounted(async () => {
  if (store.state.partCategories.selectedSubCategory) {
    checked.value = true
  }
  store.commit('ui/closeModal', close)
})
onUnmounted(() => {
  store.commit('ui/closeModal', null)
})
</script>

<style lang="scss">
.-dark [am-formula-modal] [am-first] {
  background-color: var(--color--dark-35);
}
.-light [am-formula-modal] [am-first] {
  background-color: var(--color--light-40);
}
[am-formula-modal] {
  ----font-size: 0.85rem;
  ----line-height: 1.2;
  ----spacing: 0.25rem;
  ----padding: #{var(----spacing)};

  font-size: var(----font-size);
  top: 0;
  z-index: 1000;
  position: fixed;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  [am-formula-modal-bg] {
    opacity: 0.4;
    height: 100%;
    width: 100%;
  }

  [am-formula-modal-body]>.card-body {
    overflow-y: scroll;
  }

  [am-formula-modal-body]>.card-header {
    >div {
      >p {
        flex: 1;
      }

      >svg {
        margin: 10px;

        &:hover {
          opacity: 0.7;
        }
      }
    }
  }
  [am-first], [am-error] {
    margin: 0 -16px -16px !important;
    width: calc(100% + 32px);
    padding: 0.25rem 16px;
  }
  [am-error] {
    margin: 16px -16px -16px !important;
  }

  [am-formula-modal-body] {
    position: absolute;
    max-height: 90%;
    max-width: 60%;
    width: 50%;
    overflow-x: hidden;
  }

  [am-icon-close] {
    cursor: pointer;
    width: 0.5rem;
  }

  [am-edit] {
    margin-top: 25px;
  }
}
</style>
